import React, { createContext, useContext, useEffect, useState } from "react"
import _ from 'lodash'
import { Programme } from "app/models/programmes/Programme";
import { Metric } from "app/models/metrics/Metric";

export type ProgrammeExplorerSupporterFilterMode = 'months' | 'years' | 'weeks' | 'days' | 'Q';

export type ProgrammeExplorerSupporterFilters = {
  mode: ProgrammeExplorerSupporterFilterMode
  programmes?: number[]
  campaigns?: number[],
  therapeutic_areas?: number[],
  connects?: number[],
  between_dates?: any,
  source?: string | undefined
  project_code_media?: string | null
  group_name_media?: string[]
  name_media?: string[]
  programme_title?: string
}

type BetweenDatesFilter = {
  from: string,
  to: string
}

export type ProgrammeExplorerSupporterFilterParams = {
  filter_filters: ProgrammeExplorerSupporterFilters

}

type ProgrammeExplorerSupporterContextModel = {
  params: ProgrammeExplorerSupporterFilterParams,
  metrics: ProgrammeExplorerSupporterMetrics,
  showSnapshotModal: boolean,
  programmes: Programme[],
  updateFilters: (filters: Partial<ProgrammeExplorerSupporterFilters>) => void,
  updateMetrics: (metric: string) => void
  resetFilters: () => void
  updateProgrammeExplorerProgrammes: (programmes: Programme[]) => void,
  updateShowSnapshotModal: (show: boolean) => void
  getMetricProgrammeExplorerCard: (metrics: Metric, filters: ProgrammeExplorerSupporterFilterParams) => JSX.Element[] | JSX.Element
  updateIsElearning: (value: boolean) => void
  elearning: boolean
  updateIsPodcast: (value: boolean) => void
  podcast: boolean
  metricTotals: Record<string, number | null>
  updateMetricTotal: (metricName: string, value: number | null) => void
  resetMetricTotals: () => void
  exportImages: Record<string, string>
  updateExportImage: (key: string, value: string) => void
  exportInsights: Record<string, any>
  updateExportInsights: (payload: Record<string, any>) => void
  resetExportData: () => void
  exportInsightsCardsReady: boolean
  setExportInsightsCardsReady: (ready: boolean) => void
}

const defaultFilterParams: ProgrammeExplorerSupporterFilterParams = {
  filter_filters: {
    campaigns: [],
    programmes: [],
    connects: [],
    mode: 'weeks'
  }
}

type ProgrammeExplorerSupporterMetrics = string[];

type ShowSnapshotModal = boolean;

const defaultProgrammeExplorerSupporterMetrics: ProgrammeExplorerSupporterMetrics = [];

const defaultShowSnapshotModal: ShowSnapshotModal = false;

const ProgrammeExplorerSupporterContext = createContext<ProgrammeExplorerSupporterContextModel>({
  params: defaultFilterParams,
  metrics: defaultProgrammeExplorerSupporterMetrics,
  showSnapshotModal: defaultShowSnapshotModal,
  programmes: [],
  updateFilters: (filters: Partial<ProgrammeExplorerSupporterFilters>) => { },
  updateMetrics: (metricId: string) => { },
  updateShowSnapshotModal: (show: boolean) => { },
  updateProgrammeExplorerProgrammes: (programmes: Programme[]) => { },
  resetFilters: () => { },
  getMetricProgrammeExplorerCard: (metrics: Metric, filters: ProgrammeExplorerSupporterFilterParams) => <></>,
  updateIsElearning: (value: boolean) => {},
  elearning: false,
  updateIsPodcast: (value: boolean) => {},
  podcast: false,
  metricTotals: {},
  updateMetricTotal: (metricName: string, value: number | null) => {},
  resetMetricTotals: () => {},
  exportImages: {},
  updateExportImage: (key: string, value: string) => {},
  exportInsights: {},
  updateExportInsights: (payload: Record<string, any>) => {},
  resetExportData: () => {},
  exportInsightsCardsReady: false,
  setExportInsightsCardsReady: () => {}
})


const ProgrammeExplorerSupporterProvider: React.FC = ({ children }) => {

  const [params, setParams] = useState<ProgrammeExplorerSupporterFilterParams>(defaultFilterParams)
  const [metrics, setMetrics] = useState<ProgrammeExplorerSupporterMetrics>(defaultProgrammeExplorerSupporterMetrics)
  const [showSnapshotModal, setShowSnapshotModal] = useState<ShowSnapshotModal>(defaultShowSnapshotModal)
  const [programmes, setProgrammes] = useState<Programme[]>([])
  const [elearning, setElearning] = useState<boolean>(false)
  const [podcast, setPodcast] = useState<boolean>(false)
  const [metricTotals, setMetricTotals] = useState<Record<string, number | null>>({})
  const [exportImages, setExportImages] = useState<Record<string, string>>({})
  const [exportInsights, setExportInsights] = useState<Record<string, any>>({})
  const [exportInsightsCardsReady, setExportInsightsCardsReady] = useState(false)

  // const [allowedMetrics, setAllowedMetrics] = useState<string[]>([])

  const updateMetricTotal = (metricName: string, value: number | null) => {
    setMetricTotals(prev => ({
      ...prev,
      [metricName]: value
    }))
  }

  const resetMetricTotals = () => {
    setMetricTotals({})
  }
  const updateExportImage = (key: string, value: string) => {
    try {
      console.log('[Export] updateExportImage', key, 'length:', value ? value.length : 0, 'preview:', value ? value.slice(0, 40) : null)
    } catch {}
    setExportImages(prev => ({ ...prev, [key]: value }))
  }

  const updateShowSnapshotModal = (show: boolean) => {
    setShowSnapshotModal(show)
  }
  const updateExportInsights = (payload: Record<string, any>) => {
    try {
      console.log('[Export] updateExportInsights keys:', Object.keys(payload || {}))
    } catch {}
    setExportInsights(prev => ({ ...prev, ...(payload || {}) }))
  }

  const resetExportData = () => {
    setExportImages({})
    setExportInsights({})
    setExportInsightsCardsReady(false)
  }

  const updateIsElearning = (value: boolean) => {
    setElearning(value)
  }

  const updateIsPodcast = (value: boolean) => {
    setPodcast(value)
  }


  const updateFilters = (filters: Partial<ProgrammeExplorerSupporterFilters>) => {

    let newObject = {
      ...params,
      filter_filters: {
        ...params.filter_filters,
        ...filters
      }
    }

    if (_.isEqual(params, newObject) === false) {
      setParams(newObject)
    }

  }

  const resetFilters = () => {
    setParams(defaultFilterParams)
  }

  const updateMetrics = (metricId: string) => {

    let newMetrics = metrics;

    if (newMetrics.includes(metricId)) {
      newMetrics = newMetrics.filter((metric) => metric !== metricId);
    } else {
      newMetrics.push(metricId);
    }

    setMetrics(newMetrics);
  }

  const updateProgrammeExplorerProgrammes = (programmes: Programme[]) => {
    setProgrammes(programmes)
  }
  /** Render metric card */
  const getMetricProgrammeExplorerCard = (metric: Metric, filters: ProgrammeExplorerSupporterFilterParams): JSX.Element => {

    return (<></>)

  }


  const value: ProgrammeExplorerSupporterContextModel = {
    params,
    metrics,
    showSnapshotModal,
    programmes,
    updateFilters,
    updateMetrics,
    resetFilters,
    updateProgrammeExplorerProgrammes,
    updateShowSnapshotModal,
    getMetricProgrammeExplorerCard,
    updateIsElearning,
    elearning,
    updateIsPodcast,
    podcast,
    metricTotals,
    updateMetricTotal,
    resetMetricTotals,
    exportImages,
    updateExportImage,
    exportInsights,
    updateExportInsights,
    resetExportData,
    exportInsightsCardsReady,
    setExportInsightsCardsReady
  }

  useEffect(() => {
    // console.log("do request...");
  }, [params.filter_filters])


  return <ProgrammeExplorerSupporterContext.Provider value={value}>{children}</ProgrammeExplorerSupporterContext.Provider>
}


export { ProgrammeExplorerSupporterProvider, ProgrammeExplorerSupporterContext }

export function useProgrammeExplorerSupporter() {
  return useContext(ProgrammeExplorerSupporterContext);
}
